import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilterBar,
  FilterField,
  Input,
  PageShell,
  RowActions,
  Select,
  SectionCard,
  Skeleton,
  StatusBadge,
  toast,
  type DataTableColumn,
  type RowActionItem,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { friendlyMessage } from '@/lib/errors';
import { BankAccountEditor } from '../components/BankAccountEditor';
import {
  useBankAccountMutation,
  useBankAccountQuery,
  useBankAccountsQuery,
} from '../hooks/useBankAccounts';
import { BANK_ACCOUNT_ROLES } from '../lib/bankAccountRbac';
import {
  CURRENCY_LABELS,
  EXPOSED_MALL_LABELS,
  type BankAccountPayload,
  type BankAccountSummary,
} from '../types/bankAccount';

/** 사용설정 필터 상태 — 전체/사용/미사용. */
type EnabledFilter = 'ALL' | 'ENABLED' | 'DISABLED';

const ENABLED_FILTER_LABELS: Record<EnabledFilter, string> = {
  ALL: '전체',
  ENABLED: '사용',
  DISABLED: '미사용',
};

const matchesEnabled = (row: BankAccountSummary, filter: EnabledFilter): boolean => {
  if (filter === 'ENABLED') return row.enabled;
  if (filter === 'DISABLED') return !row.enabled;
  return true;
};

const matchesKeyword = (row: BankAccountSummary, keyword: string): boolean => {
  const q = keyword.trim().toLowerCase();
  if (q === '') return true;
  return (
    row.bankName.toLowerCase().includes(q) ||
    row.accountHolder.toLowerCase().includes(q) ||
    row.accountNumber.toLowerCase().includes(q)
  );
};

/**
 * 무통장 입금 계좌 관리(M-4 ① [ID 52]). 계좌 마스터 그리드 + 등록/수정/삭제 + 검색.
 * 근거: merge-spec M-4, qa-52(deposits-admin.png). be-order admin CRUD 계약.
 * 결제 단계에서 접속 상점/통화에 맞는 계좌만 노출(M-4 ④)되므로 노출상점·통화가
 * 핵심 매핑 컬럼이다. RBAC=SUPER_ADMIN/OPS_ADMIN(권위는 BE).
 */
export const BankAccountsPage = () => {
  const canWrite = useHasRole(BANK_ACCOUNT_ROLES);

  const [keyword, setKeyword] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>('ALL');
  const [editId, setEditId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BankAccountSummary | null>(null);

  const listQuery = useBankAccountsQuery();
  const { create, update, remove } = useBankAccountMutation();
  const detailQuery = useBankAccountQuery(
    editorOpen && !creating ? editId : null,
  );

  const saving = create.isPending || update.isPending;

  const rows = useMemo(() => {
    const items = listQuery.data ?? [];
    return items.filter(
      (row) => matchesEnabled(row, enabledFilter) && matchesKeyword(row, keyword),
    );
  }, [listQuery.data, enabledFilter, keyword]);

  const openCreate = () => {
    setCreating(true);
    setEditId(null);
    setEditorOpen(true);
  };

  const openEdit = (row: BankAccountSummary) => {
    setCreating(false);
    setEditId(row.bankAccountId);
    setEditorOpen(true);
  };

  const handleSubmit = (payload: BankAccountPayload) => {
    if (!creating && editId) {
      update.mutate(
        { bankAccountId: editId, payload },
        {
          onSuccess: () => {
            toast.success('계좌가 수정되었습니다.');
            setEditorOpen(false);
          },
          onError: (err) => toast.error(friendlyMessage(err, '수정에 실패했습니다.')),
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          toast.success('계좌가 등록되었습니다.');
          setEditorOpen(false);
        },
        onError: (err) => toast.error(friendlyMessage(err, '등록에 실패했습니다.')),
      });
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.bankAccountId, {
      onSuccess: () => {
        toast.success('계좌가 삭제되었습니다.');
        setDeleteTarget(null);
      },
      onError: (err) => toast.error(friendlyMessage(err, '삭제에 실패했습니다.')),
    });
  };

  const columns: DataTableColumn<BankAccountSummary>[] = [
    {
      key: 'sortOrder',
      header: '순서',
      width: 56,
      align: 'right',
      cell: (row) => (
        <span
          className="text-[12px] tabular-nums"
          style={{ color: 'var(--content-muted)' }}
        >
          {row.sortOrder}
        </span>
      ),
    },
    {
      key: 'bankName',
      header: '은행명',
      width: 120,
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-strong)' }}>
          {row.bankName}
        </span>
      ),
    },
    {
      key: 'accountNumber',
      header: '계좌번호',
      cell: (row) => (
        <span
          className="text-[13px] tabular-nums"
          style={{ color: 'var(--content-default)' }}
        >
          {row.accountNumber}
        </span>
      ),
    },
    {
      key: 'accountHolder',
      header: '예금주',
      width: 120,
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-default)' }}>
          {row.accountHolder}
        </span>
      ),
    },
    {
      key: 'branchName',
      header: '지점명',
      width: 100,
      cell: (row) => (
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {row.branchName ?? '-'}
        </span>
      ),
    },
    {
      key: 'swiftCode',
      header: 'SWIFT',
      width: 96,
      cell: (row) => (
        <span
          className="text-[12px] tabular-nums"
          style={{ color: 'var(--content-muted)' }}
        >
          {row.swiftCode ?? '-'}
        </span>
      ),
    },
    {
      key: 'currency',
      header: '통화',
      width: 96,
      cell: (row) => (
        <span className="text-[12px]" style={{ color: 'var(--content-default)' }}>
          {CURRENCY_LABELS[row.currency]}
        </span>
      ),
    },
    {
      key: 'exposedMalls',
      header: '노출상점',
      width: 180,
      cell: (row) => (
        <span className="text-[12px]" style={{ color: 'var(--content-default)' }}>
          {row.exposedMalls.length > 0
            ? row.exposedMalls.map((m) => EXPOSED_MALL_LABELS[m]).join(' · ')
            : '-'}
        </span>
      ),
    },
    {
      key: 'enabled',
      header: '사용',
      width: 72,
      align: 'center',
      cell: (row) =>
        row.enabled ? (
          <StatusBadge label="사용" variant="info" size="sm" />
        ) : (
          <StatusBadge label="미사용" variant="neutral" size="sm" />
        ),
    },
    {
      key: 'actions',
      header: '',
      width: 56,
      align: 'right',
      cell: (row) => {
        if (!canWrite) return null;
        const actions: RowActionItem[] = [
          { key: 'edit', label: '수정', onClick: () => openEdit(row) },
          {
            key: 'delete',
            label: '삭제',
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => setDeleteTarget(row),
          },
        ];
        return <RowActions triggerLabel="작업" actions={actions} />;
      },
    },
  ];

  return (
    <PageShell
      title="무통장 입금 계좌 관리"
      breadcrumb={[{ label: '기본 설정' }, { label: '무통장 입금 계좌' }]}
      description="결제 단계에서 안내되는 무통장(직접입금) 계좌를 등록·관리합니다. 노출상점·통화에 맞는 계좌만 해당 몰 결제 화면에 노출됩니다."
      data-testid="bank-accounts-page"
    >
      <SectionCard
        title="무통장 입금 계좌"
        description="은행/계좌/예금주/지점명/SWIFT/통화/노출상점(국문·영문·중문·일문)·사용 여부를 관리합니다. 결제 단계 노출은 노출상점·통화 매핑을 따릅니다."
        aside={
          canWrite ? (
            <Button size="sm" onClick={openCreate} data-testid="bank-account-add">
              <Plus size={15} aria-hidden="true" />
              계좌 등록
            </Button>
          ) : undefined
        }
      >
        <div className="mb-4">
          <FilterBar
            filters={
              <>
                <FilterField label="검색">
                  <Input
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="은행명 / 예금주 / 계좌번호"
                    data-testid="bank-account-search"
                  />
                </FilterField>
                <FilterField label="사용설정">
                  <Select
                    value={enabledFilter}
                    onChange={(e) =>
                      setEnabledFilter(e.target.value as EnabledFilter)
                    }
                    data-testid="bank-account-enabled-filter"
                  >
                    {(['ALL', 'ENABLED', 'DISABLED'] as const).map((v) => (
                      <option key={v} value={v}>
                        {ENABLED_FILTER_LABELS[v]}
                      </option>
                    ))}
                  </Select>
                </FilterField>
              </>
            }
          />
        </div>

        {listQuery.isPending ? (
          <div role="status" aria-live="polite" className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[44px] w-full" />
            ))}
          </div>
        ) : listQuery.isError ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
            style={{
              background: 'var(--status-danger-bg)',
              color: 'var(--status-danger-fg)',
            }}
          >
            <p>계좌 목록을 불러오지 못했습니다.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void listQuery.refetch()}
            >
              다시 시도
            </Button>
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => row.bankAccountId}
            emptyState={
              (listQuery.data?.length ?? 0) === 0
                ? '등록된 무통장 입금 계좌가 없습니다.'
                : '검색 조건에 맞는 계좌가 없습니다.'
            }
            stickyHeader
            ariaLabel="무통장 입금 계좌 목록"
          />
        )}
      </SectionCard>

      <BankAccountEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        // 신규=null. 편집은 상세 GET 결과를 시드(로딩 중엔 빈 폼으로 열렸다 도착 시 리셋).
        target={creating ? null : detailQuery.data ?? null}
        saving={saving}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>무통장 입금 계좌 삭제</DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? `'${deleteTarget.bankName} ${deleteTarget.accountNumber}'`
                : ''}{' '}
              계좌를 삭제할까요? 이 작업은 되돌릴 수 없습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                취소
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={handleDelete}
            >
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
